var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../enum';
import { currencyCatalog, getCurrencySymbolFromCatalog } from '../common/currency/currencyCatalog';
export var CURRENCY_DROPDOWN_VISIBLE_ITEMS = 5;
export var CURRENCY_DROPDOWN_ITEM_HEIGHT = 40;
export var CURRENCY_DROPDOWN_MAX_HEIGHT = CURRENCY_DROPDOWN_VISIBLE_ITEMS * CURRENCY_DROPDOWN_ITEM_HEIGHT;
export var currencyAutocompleteListboxProps = {
    sx: {
        maxHeight: CURRENCY_DROPDOWN_MAX_HEIGHT,
        overflowY: 'auto',
    },
};
export var currencySelectMenuProps = {
    PaperProps: {
        sx: {
            maxHeight: CURRENCY_DROPDOWN_MAX_HEIGHT,
        },
    },
};
export var useCurrencyOptions = function () {
    return useMemo(function () {
        return currencyCatalog.map(function (item) {
            var symbol = getCurrencySymbolFromCatalog(item.id);
            return {
                id: item.id,
                symbol: symbol,
                name: "".concat(item.id),
            };
        });
    }, []);
};
/** Filters catalog options to provider-available codes. Returns [] until codes are loaded. */
export var useAvailableCurrencyOptions = function (availableCodes) {
    var allOptions = useCurrencyOptions();
    return useMemo(function () {
        if (!availableCodes)
            return [];
        var available = new Set(availableCodes.map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean));
        return allOptions.filter(function (opt) { return available.has(opt.id.toUpperCase()); });
    }, [allOptions, availableCodes]);
};
/** Sentinel option id selected to open the "add a custom currency" dialog. */
export var OTHER_CURRENCY_ID = '__OTHER__';
/** Appends the "Other" sentinel entry (opens the custom-currency dialog) to the end of a list. */
export var withOtherOption = function (options, label) { return __spreadArray(__spreadArray([], options, true), [
    { id: OTHER_CURRENCY_ID, name: label, symbol: '' },
], false); };
/** Merges user-added custom currency codes into a list of options, symbol falling back to the code. */
export var withCustomCurrencyOptions = function (options, customCodes) {
    if (!(customCodes === null || customCodes === void 0 ? void 0 : customCodes.length))
        return options;
    var known = new Set(options.map(function (o) { return o.id.toUpperCase(); }));
    var additions = customCodes
        .map(function (c) { return c.trim().toUpperCase(); })
        .filter(function (code) { return code && !known.has(code); })
        .map(function (code) { return ({ id: code, name: code, symbol: code }); });
    return additions.length ? __spreadArray(__spreadArray([], options, true), additions, true) : options;
};
/** Ensures the current (possibly unsupported) value remains selectable so Autocomplete does not blank. */
export var withCurrentCurrencyOption = function (options, currentCode) {
    var _a;
    var code = typeof currentCode === 'string' ? currentCode === null || currentCode === void 0 ? void 0 : currentCode.trim().toUpperCase() : '';
    if (!code)
        return options;
    if (options.some(function (o) { return o.id.toUpperCase() === code; }))
        return options;
    var catalogItem = currencyCatalog.find(function (c) { return c.id.toUpperCase() === code; });
    return __spreadArray(__spreadArray([], options, true), [
        {
            id: code,
            name: code,
            symbol: getCurrencySymbolFromCatalog((_a = catalogItem === null || catalogItem === void 0 ? void 0 : catalogItem.id) !== null && _a !== void 0 ? _a : code),
        },
    ], false);
};
/** Currencies selectable as an org's roll-up reporting currency. */
export var ROLLUP_CURRENCY_CODES = ['AUD', 'CAD', 'CHF', 'CNY', 'EUR', 'GBP', 'HKD', 'JPY', 'PHP', 'SGD', 'USD'];
/** Roll-up currency options restricted to ROLLUP_CURRENCY_CODES, keeping an out-of-list current value selectable. */
export var useRollupCurrencyOptions = function (currentCode) {
    var allOptions = useCurrencyOptions();
    return useMemo(function () {
        var restricted = allOptions.filter(function (opt) { return ROLLUP_CURRENCY_CODES.includes(opt.id.toUpperCase()); });
        return withCurrentCurrencyOption(restricted, currentCode);
    }, [allOptions, currentCode]);
};
export var isCurrencySupported = function (currencyCode, availableCodes, customCodes) {
    if (typeof currencyCode !== 'string' || !currencyCode.trim())
        return true;
    var code = currencyCode.trim().toUpperCase();
    if (customCodes === null || customCodes === void 0 ? void 0 : customCodes.some(function (c) { return c.trim().toUpperCase() === code; }))
        return true;
    if (!availableCodes)
        return true;
    return availableCodes.some(function (c) { return c.trim().toUpperCase() === code; });
};
export var useCurrencySymbol = function (currencyCode) {
    var _a, _b, _c;
    var i18n = useTranslation().i18n;
    var locale = (_a = i18n.resolvedLanguage) !== null && _a !== void 0 ? _a : Language.EN;
    var isFr = locale === Language.FR;
    var currencyOptions = useCurrencyOptions();
    var option = currencyOptions.find(function (c) { return c.id === currencyCode; });
    var currencySymbol = (_c = (_b = option === null || option === void 0 ? void 0 : option.symbol) !== null && _b !== void 0 ? _b : currencyCode) !== null && _c !== void 0 ? _c : '';
    return {
        currencySymbol: currencySymbol,
        prefix: isFr ? undefined : currencySymbol,
        suffix: isFr ? currencySymbol : undefined,
    };
};
